import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, CheckCircle, ImagePlus, Trash2 } from 'lucide-react';
import { profileAPI } from '../../api/profile';
import { getStorageUrl } from '../../utils/storage';
import { Card } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';

export default function UploadPhotoPage() {
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [userName, setUserName]         = useState('');
  const [preview, setPreview]           = useState(null);   // local blob URL
  const [file, setFile]                 = useState(null);
  const [uploading, setUploading]       = useState(false);
  const [success, setSuccess]           = useState(false);
  const [apiError, setApiError]         = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const inputRef = useRef();

  // API #6 — load current photo
  useEffect(() => {
    profileAPI
      .getProfile()
      .then((res) => {
        const data = res.data.data;
        setCurrentPhoto(getStorageUrl(data.profile_photo));
        setUserName(data.name || '');
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    // Client-side: only jpg/png, max 15 MB
    if (!['image/jpeg', 'image/png'].includes(selected.type)) {
      setApiError('Only JPG and PNG images are allowed.');
      return;
    }
    if (selected.size > 15 * 1024 * 1024) {
      setApiError('Image must be smaller than 15 MB.');
      return;
    }

    setApiError('');
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const clearSelection = () => {
    setFile(null);
    setPreview(null);
    setApiError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  // API #7 — upload photo
  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setApiError('');

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const res = await profileAPI.uploadPhoto(formData);
      const newUrl = getStorageUrl(res.data.data.profile_photo);
      setCurrentPhoto(newUrl);
      clearSelection();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const msg = err.response?.data?.errors?.photo?.[0]
        || err.response?.data?.message
        || 'Upload failed. Please try again.';
      setApiError(msg);
    } finally {
      setUploading(false);
    }
  };

  // Avatar helpers
  const AvatarDisplay = ({ src, name, size = 'lg' }) => {
    const dim   = size === 'lg' ? 'w-28 h-28 text-4xl' : 'w-20 h-20 text-2xl';
    const label = name?.[0]?.toUpperCase() || 'U';
    return src ? (
      <img
        src={src}
        alt="Profile"
        className={`${dim} rounded-3xl object-cover`}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      <div
        className={`${dim} rounded-3xl flex items-center justify-center font-serif font-bold text-white`}
        style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
      >
        {label}
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Profile Photo" subtitle="Upload or change your photo" showBack />

      <div className="px-4 py-4 pb-6 flex flex-col gap-4">

        {/* Current photo */}
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
            Current Photo
          </p>

          {loadingProfile ? (
            <div className="flex justify-center py-6">
              <div className="w-8 h-8 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="flex justify-center">
              <AvatarDisplay src={currentPhoto} name={userName} size="lg" />
            </div>
          )}
        </Card>

        {/* Upload section */}
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
            New Photo
          </p>

          {/* Success banner */}
          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"
            >
              <CheckCircle size={16} />
              Profile photo updated successfully!
            </motion.div>
          )}

          {/* Error banner */}
          {apiError && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600"
            >
              {apiError}
            </motion.div>
          )}

          {/* Hidden file input */}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={handleFileChange}
          />

          {preview ? (
            /* Preview of selected image */
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <AvatarDisplay src={preview} name={userName} size="lg" />
              <p className="text-xs text-gray-500">{file?.name}</p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={clearSelection}
                  className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-gray-200 py-3 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <Trash2 size={14} />
                  Remove
                </button>
                <Button
                  onClick={handleUpload}
                  loading={uploading}
                  className="flex-1"
                >
                  Upload Photo
                </Button>
              </div>
            </motion.div>
          ) : (
            /* Pick photo button */
            <button
              onClick={() => inputRef.current?.click()}
              className="w-full flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-amber-200 py-8 hover:border-amber-400 hover:bg-amber-50 transition-colors"
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
              >
                <ImagePlus size={22} color="white" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700">
                  Tap to choose a photo
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  JPG or PNG · max 15 MB
                </p>
              </div>
            </button>
          )}
        </Card>

        {/* Info note */}
        <div className="flex items-start gap-2 px-1">
          <Camera size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-400 leading-relaxed">
            Your photo is visible on your profile. Use a clear, front-facing image for best results.
          </p>
        </div>
      </div>
    </div>
  );
}
